import React from "react";
import { Link } from "react-router-dom";
import Seo from "../../../components/public/Seo";
import "../../../styles/legal.css";

export default function PrivacyPolicy() {
  return (
    <>
      <Seo
        title="Privacy Policy — Veya"
        description="How Veya collects, uses, stores and shares personal information."
      />

      <main className="legal-page">
        <section className="legal-hero legal-hero--narrow">
          <p className="legal-kicker">Privacy Policy</p>
          <h1>Privacy Policy</h1>
          <p>
            This policy explains how Veya collects, uses, stores and shares
            personal information when people use our shopping, styling, try-on,
            merchant and catalogue features.
          </p>
          <p className="legal-updated">Last updated: May 2026</p>
        </section>

        <article className="legal-document">
          <section>
            <h2>1. Who we are</h2>
            <p>
              Veya is a digital fashion experience platform designed to help
              shoppers discover, style, save and visualise fashion from boutique
              retailers.
            </p>
            <p>
              In this policy, “Veya”, “we”, “us” and “our” refer to the Veya
              platform. “You” refers to shoppers, merchants, visitors and other
              people who use or interact with Veya.
            </p>
          </section>

          <section>
            <h2>2. Information we collect</h2>
            <p>
              The information we collect depends on how you use Veya. It may
              include:
            </p>
            <ul>
              <li>
                account information, such as your name, email address, login
                details and account preferences;
              </li>
              <li>
                merchant information, such as business name, contact details,
                catalogue information, product data and onboarding details;
              </li>
              <li>
                product and catalogue information, including titles,
                descriptions, prices, images, categories, materials, colours,
                product links and availability information;
              </li>
              <li>
                shopper activity, such as products viewed, saved, tried on,
                added to a bag, removed from a bag, or included in edits;
              </li>
              <li>
                edit and styling information, such as saved outfits, moodboards,
                notes, selected items, visual signals and style preferences
                created through Veya;
              </li>
              <li>
                images or files you upload or select for Veya features,
                including where those files are needed for try-on, catalogue or
                styling functionality;
              </li>
              <li>
                support and communication information, such as messages you send
                to us;
              </li>
              <li>
                technical information, such as device type, browser, IP address,
                approximate location, usage logs, error logs and performance
                data.
              </li>
            </ul>
          </section>

          <section>
            <h2>3. How we use information</h2>
            <p>We may use information to:</p>
            <ul>
              <li>create, manage and secure shopper and merchant accounts;</li>
              <li>operate Veya’s browse, edit, save, bag and try-on features;</li>
              <li>
                display product, boutique and catalogue information to shoppers;
              </li>
              <li>
                support merchant onboarding, catalogue management and product
                visibility;
              </li>
              <li>
                generate merchant insights about product interest, styling
                signals, saved items, try-ons, product demand and catalogue
                opportunities;
              </li>
              <li>
                analyse visual, style and product signals to improve discovery,
                recommendations and platform quality;
              </li>
              <li>
                provide customer support, respond to enquiries and communicate
                service updates;
              </li>
              <li>detect errors, protect security and prevent misuse;</li>
              <li>
                comply with legal obligations and enforce our terms and
                policies.
              </li>
            </ul>
          </section>

          <section>
            <h2>4. AI, image analysis and automated processing</h2>
            <p>
              Veya may use automated tools, including AI-assisted analysis, to
              process product images, edit images, catalogue information,
              garment attributes, shopper interactions and style signals.
            </p>
            <p>
              This processing may help power features such as visual try-on,
              product discovery, saved edits, styling intelligence, merchant
              insights and catalogue analysis.
            </p>
            <p>
              Veya try-on is for visualisation only. Real fit, sizing, colour,
              fabric behaviour and garment appearance may vary.
            </p>
          </section>

          <section>
            <h2>5. Merchant insights</h2>
            <p>
              Veya may provide merchants with insights based on shopper
              interactions with products, edits and try-on features. These may
              include signals such as product interest, saves, try-ons,
              add-to-bag activity, style clustering, colour interest and
              catalogue opportunities.
            </p>
            <p>
              Where possible, we aim to provide merchants with aggregated,
              contextual or product-level insights rather than unnecessary
              personal information about individual shoppers.
            </p>
          </section>

          <section>
            <h2>6. Sharing information</h2>
            <p>
              We may share information with service providers and partners who
              help us operate Veya. These may include hosting providers, cloud
              storage providers, database providers, email providers, analytics
              providers, security tools, payment or checkout providers if added,
              AI processing providers, and technical support providers.
            </p>
            <p>
              We may also share relevant information with merchants where needed
              to operate merchant-facing services, catalogue features, product
              links, customer support, analytics or insights.
            </p>
            <p>
              We may disclose information if required by law, to protect our
              rights, to investigate misuse, or in connection with a business
              sale, merger, restructuring or similar event.
            </p>
          </section>

          <section>
            <h2>7. Storage, security and international providers</h2>
            <p>
              Veya may use service providers located in New Zealand or overseas.
              This means information may be stored or processed outside New
              Zealand.
            </p>
            <p>
              We take reasonable steps to protect information from loss,
              unauthorised access, misuse or disclosure. No online service can
              guarantee complete security, but we aim to use appropriate
              technical and organisational safeguards for the nature of the
              information we hold.
            </p>
          </section>

          <section>
            <h2>8. How long we keep information</h2>
            <p>
              We keep information for as long as reasonably needed to provide
              Veya, maintain records, meet legal obligations, resolve disputes,
              improve the platform and enforce our terms.
            </p>
            <p>
              Some information may remain in backups, logs or archived records
              for a limited period after deletion from active systems.
            </p>
          </section>

          <section>
            <h2>9. Access, correction and deletion</h2>
            <p>
              You may contact us to request access to personal information we
              hold about you, ask us to correct it, or request deletion where
              appropriate.
            </p>
            <p>
              We may need to verify your identity before actioning a request. In
              some cases we may need to keep certain information for legal,
              security, accounting or operational reasons.
            </p>
          </section>

          <section>
            <h2>10. Cookies and analytics</h2>
            <p>
              Veya may use cookies, local storage and similar technologies to
              keep users signed in, remember preferences, operate core features,
              understand usage, improve performance and support security.
            </p>
            <p>
              More information is available in our{" "}
              <Link to="/legal/cookies">Cookies & Analytics Notice</Link>.
            </p>
          </section>

          <section>
            <h2>11. Children</h2>
            <p>
              Veya is not designed for children. If you believe a child has
              provided personal information to Veya without appropriate consent,
              please contact us.
            </p>
          </section>

          <section>
            <h2>12. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The updated
              version will be posted on this page with a new “Last updated”
              date.
            </p>
          </section>

          <section>
            <h2>13. Contact</h2>
            <p>
              For privacy questions, access or correction requests, or concerns
              about how Veya handles personal information, contact:
            </p>
            <p>
              <a href="mailto:hello@veya.co.nz">hello@veya.co.nz</a>
            </p>
          </section>
        </article>
      </main>
    </>
  );
}